import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";
import { formatTimeDisplay, isoDate } from "@/lib/schedule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Schedule an assessment for THIS profile (lead/prospect/member) into a slot.
// Pick which kids + a time; books directly — no Pending-inbox detour.
export default function ScheduleAssessmentModal({ lead, onClose, onDone }) {
    const athletes = lead.athletes || [];
    const [checked, setChecked] = useState(() => new Set(athletes.map((a) => a.athlete_id)));
    const [slots, setSlots] = useState(null); // null = loading
    const [filter, setFilter] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const today = new Date();
                const to = new Date();
                to.setDate(to.getDate() + 28);
                const r = await axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: isoDate(today), to_date: isoDate(to) },
                    withCredentials: true,
                });
                setSlots(r.data || []);
            } catch (e) {
                toast.error("Couldn't load slots", { description: e?.response?.data?.detail });
                setSlots([]);
            }
        })();
    }, []);

    const toggle = (id) => {
        setChecked((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const pickCount = checked.size;
    const candidates = (slots || []).filter((s) => (s.booking_count || 0) + pickCount <= s.capacity);
    const f = filter.trim().toLowerCase();
    const filtered = !f ? candidates
        : candidates.filter((s) => `${s.date} ${s.start_time} ${s.label || ""}`.toLowerCase().includes(f));

    const book = async (slot) => {
        if (busy) return;
        if (pickCount === 0) { toast.error("Pick at least one athlete"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/coach/leads/${lead.user_id}/schedule-assessment`,
                { slot_id: slot.slot_id, athlete_ids: [...checked] },
                { withCredentials: true });
            toast.success("Assessment scheduled", {
                description: `${slot.date} · ${formatTimeDisplay(slot.start_time)}`,
            });
            onDone?.();
        } catch (e) {
            toast.error("Couldn't schedule", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" data-testid="schedule-assessment-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] flex flex-col">
                <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                    <CalendarPlus className="w-5 h-5 text-brand-red" /> Schedule assessment
                </h3>
                <p className="text-xs text-zinc-500 mb-3">Pick who's coming, then choose a time.</p>

                {athletes.length === 0 ? (
                    <p className="text-sm text-amber-300" data-testid="sa-no-athletes">
                        This profile has no athletes yet — add one first, then schedule.
                    </p>
                ) : (
                    <>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {athletes.map((a) => (
                                <button
                                    key={a.athlete_id}
                                    type="button"
                                    data-testid={`sa-athlete-${a.athlete_id}`}
                                    onClick={() => toggle(a.athlete_id)}
                                    className={`text-xs px-3 py-1.5 border rounded-full transition-colors ${
                                        checked.has(a.athlete_id)
                                            ? "bg-brand-red text-white border-brand-red"
                                            : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                                    }`}
                                >
                                    {a.first_name} {a.last_name || ""}
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder="Filter by date, time, or label…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm mb-3"
                            data-testid="sa-filter"
                        />
                        <div className="overflow-y-auto flex-1 border border-zinc-800 rounded divide-y divide-zinc-900 min-h-[120px]">
                            {slots === null ? (
                                <p className="px-3 py-6 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading slots…
                                </p>
                            ) : filtered.length === 0 ? (
                                <p className="px-3 py-6 text-center text-sm text-zinc-500" data-testid="sa-no-slots">
                                    No open slots for {pickCount} athlete(s) in the next 4 weeks.
                                </p>
                            ) : (
                                filtered.map((s) => (
                                    <button
                                        key={s.slot_id}
                                        type="button"
                                        disabled={busy}
                                        onClick={() => book(s)}
                                        data-testid={`sa-slot-${s.slot_id}`}
                                        className="w-full text-left px-3 py-2.5 hover:bg-zinc-900 disabled:opacity-50 flex items-center justify-between gap-3"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm text-white">{s.date} · {formatTimeDisplay(s.start_time)}</div>
                                            <div className="text-xs text-zinc-500 truncate">{s.label || "Session"}</div>
                                        </div>
                                        <span className="text-[11px] font-mono text-zinc-400 shrink-0">{s.booking_count}/{s.capacity}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white" data-testid="sa-cancel">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
